import { useEffect } from 'react';
import { Checkbox, DatePicker, Input, InputNumber, Mentions, Modal, Radio, Select, Space, Switch, Typography } from 'antd';
import { CrownOutlined } from '@ant-design/icons';
import RichSelectOption from './richSelectOption.jsx';

const { Text } = Typography;

const { TextArea } = Input;

const CURRENCY_OPTIONS = [
  { label: '$', value: '$' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'INR', value: 'INR' },
];

const PERIOD_OPTIONS = [
  { label: 'Hourly', value: 'Hourly' },
  { label: 'Daily', value: 'Daily' },
  { label: 'Weekly', value: 'Weekly' },
  { label: 'Monthly', value: 'Monthly' },
  { label: 'Yearly', value: 'Yearly' },
];

export function normalizeSelectOptions(options = []) {
  return options.map((option) => {
    if (!option || typeof option !== 'object') return option;

    const rawValue = [
      option.value,
      option.user_id,
      option.userId,
      option.USER_ID,
      option.legacyUserId,
      option.id,
      option._id,
    ].find((candidate) => (
      candidate !== undefined
      && candidate !== null
      && String(candidate).trim() !== ''
    ));
    const value = rawValue === undefined || rawValue === null ? '' : String(rawValue).trim();

    return { ...option, value };
  }).filter((option) => (
    option === undefined
    || option === null
    || typeof option !== 'object'
    || String(option.value ?? '').trim() !== ''
  ));
}

function InputField({ field, value, onChange }) {
  return (
    <Input
      value={value}
      onChange={onChange}
      placeholder={field.placeholder}
      maxLength={field.maxLength}
      prefix={<CrownOutlined />}
    />
  );
}

function TextareaField({ field, value, onChange }) {
  return (
    <TextArea
      value={value}
      onChange={onChange}
      rows={field.rows ?? 5}
      placeholder={field.placeholder}
    />
  );
}

function SelectField({ field, options, value, onChange }) {
  const normalizedOptions = normalizeSelectOptions(options);
  return (
    <Select
      showSearch
      value={value}
      onChange={onChange}
      options={normalizedOptions}
      placeholder={field.placeholder}
      className="dynamic-popup__field-select"
      optionFilterProp="label"
      prefix={<CrownOutlined />}
    />
  );
}

function MultiSelectField({ field, options, value, onChange }) {
  const normalizedOptions = normalizeSelectOptions(options);
  return (
    <Select
      mode="multiple"
      showSearch
      value={value}
      onChange={onChange}
      options={normalizedOptions}
      placeholder={field.placeholder}
      className="dynamic-popup__field-select"
      optionFilterProp="label"
      prefix={<CrownOutlined />}
    />
  );
}

function SearchSelectField({ field, options, onSearch, value, onChange, popupContext }) {
  const normalizedOptions = normalizeSelectOptions(options);
  const layout = field.optionLayout;
  const optionsByValue = new Map(normalizedOptions.map((option) => [String(option?.value ?? ''), option]));

  async function handleChange(nextValue, option) {
    const selectedOption = option ?? optionsByValue.get(String(nextValue ?? ''));
    if (typeof field.onSelectGuard === 'function') {
      const result = await field.onSelectGuard({
        value: nextValue,
        option: selectedOption,
        field,
        popupContext,
        confirm: Modal.confirm,
      });
      if (result === false) return;
    }
    onChange?.(nextValue);
  }

  return (
    <Select
      showSearch
      filterOption={false}
      notFoundContent={field.notFoundContent ?? null}
      value={value}
      onChange={handleChange}
      options={normalizedOptions}
      placeholder={field.placeholder}
      onSearch={onSearch}
      className={`dynamic-popup__field-select${layout ? ' dynamic-popup__field-select--rich' : ''}`}
      popupClassName={layout ? 'dynamic-popup__option-dropdown' : undefined}
      listHeight={layout ? 340 : undefined}
      optionLabelProp="label"
      // Rich rows apply to the DROPDOWN only ??? the closed select keeps the plain
      // one-line `label`, so a selected value never blows up the field height.
      optionRender={layout ? (option) => <RichSelectOption option={option?.data ?? option} layout={layout} /> : undefined}
      prefix={<CrownOutlined />}
    />
  );
}

function MentionField({ field, mentionOpts, onMentionSearch, value, onChange }) {
  // maxLength is admin config on the popup field. Enforced in TWO places on
  // purpose: the browser stops the typing here, and securityValidationRule
  // re-checks the same `field.maxLength` at submit — because a value can also
  // arrive by paste, autofill or a prefill, none of which the input cap sees.
  const limit = Number(field.maxLength) > 0 ? Number(field.maxLength) : undefined;
  const used = String(value ?? '').length;
  return (
    <>
      <Mentions
        value={value}
        onChange={onChange}
        rows={field.rows ?? 5}
        placeholder={field.placeholder}
        prefix="@"
        options={mentionOpts}
        onSearch={onMentionSearch}
        maxLength={limit}
        className="dynamic-popup__mention-textarea"
      />
      {limit ? (
        // Shown always, not only once exceeded: a limit the user discovers by
        // hitting it is a limit that has already cost them their sentence.
        <div
          className="dynamic-popup__char-count"
          style={{ textAlign: 'right', fontSize: 12, marginTop: 4, color: used > limit * 0.9 ? '#b45309' : '#8c8c8c' }}
        >
          {used} / {limit}
        </div>
      ) : null}
    </>
  );
}

/**
 * Composite rate input: single amount or range, with currency and period selectors.
 * Receives `value`/`onChange` from Ant Design Form.Item (controlled component protocol).
 */
function CandidateRateField({ field = {}, value = {}, onChange }) {
  const period = value.period ?? 'Hourly';
  const isRange = value.isRange ?? false;

  function update(patch) {
    onChange?.({ ...value, ...patch });
  }

  // Seed the sensible defaults ($ / Hourly) into the actual form value the FIRST
  // time the field renders, so they're submitted even if the user never touches
  // the currency/period selects.
  useEffect(() => {
    const seed = {};
    if (value.currency == null) seed.currency = '$';
    if (value.period == null) seed.period = 'Hourly';
    if (Object.keys(seed).length) onChange?.({ ...value, ...seed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inline hints for the two rate rules (the Form.Item validator also blocks
  // submit — see the others-assign candidateRate validator): To > From, and the
  // rate must not exceed the job's client rate (field.rateCeiling, injected from
  // the job's jobPayDetails).
  const entered = isRange ? Number(value.maxAmount) : Number(value.amount);
  const rangeInvalid = isRange && value.minAmount != null && value.maxAmount != null
    && Number(value.maxAmount) <= Number(value.minAmount);
  const overCeiling = field.rateCeiling != null && Number.isFinite(entered)
    && entered > Number(field.rateCeiling);

  return (
    <div>
      <Space.Compact style={{ width: '100%' }}>
        {isRange ? (
          <>
            <InputNumber
              controls={false}
              placeholder="From"
              value={value.minAmount}
              onChange={(v) => update({ minAmount: v })}
              style={{ flex: 1 }}
            />
            <InputNumber
              controls={false}
              placeholder="To"
              value={value.maxAmount}
              onChange={(v) => update({ maxAmount: v })}
              style={{ flex: 1 }}
            />
          </>
        ) : (
          <InputNumber
            controls={false}
            placeholder="0"
            value={value.amount}
            onChange={(v) => update({ amount: v })}
            style={{ flex: 2 }}
          />
        )}
        <Select
          value={value.currency ?? '$'}
          options={CURRENCY_OPTIONS}
          onChange={(v) => update({ currency: v })}
          style={{ width: 80 }}
        />
        <Select
          value={period}
          options={PERIOD_OPTIONS}
          onChange={(v) => update({ period: v })}
          style={{ width: 110 }}
        />
      </Space.Compact>
      {(rangeInvalid || overCeiling) && (
        <div style={{ marginTop: 4 }}>
          {rangeInvalid && <Text type="danger" style={{ fontSize: 12, display: 'block' }}>To rate must be greater than From rate.</Text>}
          {overCeiling && <Text type="danger" style={{ fontSize: 12, display: 'block' }}>Rate must not exceed the client rate ({field.rateCeiling}).</Text>}
        </div>
      )}
      <Space size="small" style={{ marginTop: 8 }}>
        <Switch checked={isRange} onChange={(v) => update({ isRange: v })} />
        <span>Range</span>
      </Space>
    </div>
  );
}

function DatePickerField({ field, value, onChange }) {
  return (
    <DatePicker
      value={value}
      onChange={onChange}
      placeholder={field.placeholder ?? 'Select date'}
      style={{ width: '100%' }}
      format="YYYY-MM-DD"
    />
  );
}

function PriorityCheckboxField({ field, checked, onChange }) {
  return <Checkbox checked={checked} onChange={onChange}>{field.checkboxLabel}</Checkbox>;
}

function NumberField({ field, value, onChange }) {
  return (
    <InputNumber
      value={value}
      onChange={onChange}
      min={field.min}
      max={field.max}
      placeholder={field.placeholder}
      style={{ width: '100%' }}
    />
  );
}

function RadioGroupField({ options, value, onChange }) {
  return <Radio.Group value={value} onChange={onChange} options={options} />;
}

/**
 * Maps field `type` strings to their React components.
 *
 * Each component receives the standard field props injected by PopupFields:
 *   `{ field, options, onSearch, mentionOpts, onMentionSearch }`
 * plus `value`/`onChange` (or `checked`/`onChange` for priority-checkbox)
 * injected by Ant Design Form.Item.
 *
 * To add a new field type: add a component here and register it.
 */
export const FIELD_REGISTRY = {
  input: InputField,
  text: InputField,
  textarea: TextareaField,
  select: SelectField,
  'multi-select': MultiSelectField,
  'search-select': SearchSelectField,
  'mention-textarea': MentionField,
  'candidate-rate': CandidateRateField,
  'priority-checkbox': PriorityCheckboxField,
  'date-picker': DatePickerField,
  'number': NumberField,
  'radio': RadioGroupField,
};

/**
 * Returns extra Form.Item props needed for a given field type.
 * @param {string} fieldType
 * @returns {Object}
 */
export function getFieldFormItemProps(fieldType) {
  if (fieldType === 'priority-checkbox') return { valuePropName: 'checked' };
  return {};
}
